import React, { useMemo } from 'react';

import { inRn, pluralize } from 'lib/helpers/utility';

import Allowed from 'components/shared/Permissions/Allowed';

import { TableFooterSummaryItem } from '../ReceiptTable/components/TableFooterSummary';

import { calculateTotals } from './lib/helpers';

const ReceiptSummary = ({ transaction, transactions, isPatient, unallocatedAmount = 0 }) => {
  const { id, amount_paid_self, surcharge_amount, type, amount_refunded } = transaction || {};

  const isWriteoff = type === 'writeoff';

  const surchargeAmount = inRn(surcharge_amount);
  const amountPaid = inRn(amount_paid_self) - surchargeAmount;

  const amountPaidWithSurcharge = amountPaid + surchargeAmount;

  const totals = useMemo(() => calculateTotals(transactions), [transactions]);

  const previousPayments = totals.totalAmountPaid - (!isWriteoff ? amountPaid : 0);

  const hasInvoiceOrManyTransactions =
    !!transaction?.invoice || (transactions.length > 0 && transaction?.id !== transactions[0]?.id);

  const amountDue = transactions?.reduce((acc, t) => {
    if (!t.invoice) return acc;

    const { balance = 0 } = t?.invoice || {};

    return acc + balance + (isWriteoff ? amountPaid : 0);
  }, 0);

  return (
    <Allowed requiredPermissions={isPatient ? '' : 'payments.read'}>
      <div className="flex w-full max-w-[332px] flex-col items-end gap-4 self-end sm:max-w-full">
        <div className="flex w-full flex-col border-0 border-b border-solid border-b-neutral-900 py-1">
          <TableFooterSummaryItem
            label="Subtotal"
            value={
              !hasInvoiceOrManyTransactions
                ? amountPaid + (amount_refunded || 0)
                : totals.totalAmount
            }
          />
          <TableFooterSummaryItem label="Technology fee" value={surchargeAmount} />
          {amount_refunded > 0 && (
            <TableFooterSummaryItem label="Refund" value={amount_refunded * -1} />
          )}
          {hasInvoiceOrManyTransactions && (
            <>
              {totals?.totalWriteOff > 0 && !isWriteoff && (
                <TableFooterSummaryItem label="Write Off" value={totals.totalWriteOff * -1} />
              )}

              <TableFooterSummaryItem label="Discount" value={totals.totalDiscount * -1} />

              <TableFooterSummaryItem label="Tax" value={totals.totalTax} />

              {previousPayments > 0 && (
                <TableFooterSummaryItem label="Previous payments" value={previousPayments * -1} />
              )}
            </>
          )}
          <TableFooterSummaryItem
            label="Current amount due"
            value={
              hasInvoiceOrManyTransactions
                ? amountDue + amountPaidWithSurcharge
                : totals.totalBalance + amountPaidWithSurcharge
            }
          />
          {id && (
            <TableFooterSummaryItem
              className="h-10"
              label="Total amount paid"
              value={amountPaidWithSurcharge}
              color="neutral-900"
            />
          )}
          <div className="border-0 border-b border-solid border-b-neutral-900" />
        </div>
        {id && (
          <div className="flex w-full flex-col rounded-md bg-neutral-50 px-2">
            <TableFooterSummaryItem
              label={`Remaining invoice ${pluralize('balance', transactions?.length)}`}
              value={totals?.totalBalance}
              valueClassName="text-neutral-900"
            />
            {unallocatedAmount > 0 && (
              <TableFooterSummaryItem
                label="Unallocated amount"
                value={unallocatedAmount}
                valueClassName="text-neutral-900"
              />
            )}
          </div>
        )}
      </div>
    </Allowed>
  );
};

export default ReceiptSummary;
